import { computed, reactive, ref } from "vue";
import { findDescendants, findIndeterminate } from "@/Utils/hierarchy";

export { default as LinkedTo } from "./LinkedTo.vue";
export { default as Hierarchy } from "./Hierarchy.vue";

export const LinkedToType = Object.freeze({
    SITE: "site",
    SPACES: "spaces",
    ASSETS: "assets",
});

export const LinkedToStep = Object.freeze({
    SITES: "sites",
    TYPES: "types",
    ASSETS: "assets",
    SPACES: "spaces",
    SEARCH: "search",
});

export const ChildType = Object.freeze({
    SPACE: "space",
    ASSET: "asset",
    ASSET_GROUP: "asset_group",
});

export const useLinkedTo = (initialState, sites, checkDescendants) => {
    /**
     * Keep track of the current navigation steps and choices made
     * for each step.
     */
    const navigationState = reactive({
        steps: [LinkedToStep.SITES],
        siteId: null,
        parentIds: [],
    });

    /**
     * Keep track of the current search being performed.
     */
    const searchQuery = ref("");

    /**
     * Transform the given initial state into the needed internal format.
     */
    const prepareInitialState = computed(() => {
        if (!initialState) {
            return {};
        }

        return sites.value.reduce((state, site) => {
            const children = site.children.filter((child) => {
                return initialState.children.includes(child.id);
            });

            const spaces = [];
            const assets = [];

            children.forEach((child) => {
                if (child.type === ChildType.SPACE) {
                    spaces.push(child.id);
                } else {
                    assets.push(child.id);
                }
            });

            state[site.id] = {
                site: initialState.sites.includes(site.id),
                spaces,
                assets,
            };

            return state;
        }, {});
    });

    /**
     * Keep track of which children for each site have been checked.
     * {
     *     [siteId]: {
     *         site: false,
     *         // Arrays of IDs
     *         spaces: [],
     *         assets: [],
     *     }
     * }
     */
    const internalState = ref(prepareInitialState.value);

    /**
     * Determine which site is currently active for the next navigation steps.
     */
    const currentSite = computed(() => {
        if (!navigationState.siteId) {
            return null;
        }

        return sites.value.find((s) => s.id === navigationState.siteId);
    });

    /**
     * Filter children of the currently active site and exclude
     * all except spaces.
     */
    const currentSiteSpaces = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        return currentSite.value.children.filter(
            (child) => child.type === ChildType.SPACE
        );
    });

    /**
     * Filter children of the currently active site and exclude
     * all except assets and asset groups.
     */
    const currentSiteAssets = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        return currentSite.value.children.filter(
            (child) =>
                child.type === ChildType.ASSET ||
                child.type === ChildType.ASSET_GROUP
        );
    });

    /**
     * Determine which spaces of the currently active site have been checked.
     */
    const currentSiteCheckedSpaces = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        return internalState.value[currentSite.value.id][LinkedToType.SPACES];
    });

    /**
     * Determine which assets and asset groups of the currently active site have been checked.
     */
    const currentSiteCheckedAssets = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        return internalState.value[currentSite.value.id][LinkedToType.ASSETS];
    });

    /**
     * Determine which children of the currently active site have children and are
     * considered parents.
     */
    const currentSiteParentIds = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        return currentSite.value.children
            .filter((child) => child.parentId !== null)
            .map((child) => child.parentId);
    });

    /**
     * Determine which children of the currently active site have been checked.
     */
    const currentSiteCheckedChildrenIds = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        if (!internalState.value[currentSite.value.id]) {
            return [];
        }

        return Object.values(internalState.value[currentSite.value.id]).flat();
    });

    /**
     * Based on the checked children for the currently active site, determine which
     * children should be treated as indeterminate.
     */
    const currentSiteIndeterminateChildrenIds = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        return findIndeterminate(
            currentSite.value.children,
            currentSiteCheckedChildrenIds.value
        );
    });

    /**
     * Determine which types of the active site have been checked.
     */
    const currentSiteCheckedTypes = computed(() => {
        const types = [];

        if (
            currentSiteCheckedSpaces.value.length !== 0 &&
            currentSiteCheckedSpaces.value.length ===
                currentSiteSpaces.value.length
        ) {
            types.push(LinkedToType.SPACES);
        }

        if (
            currentSiteCheckedAssets.value.length !== 0 &&
            currentSiteCheckedAssets.value.length ===
                currentSiteAssets.value.length
        ) {
            types.push(LinkedToType.ASSETS);
        }

        return types;
    });

    /**
     * Based on the checked spaces and assets of the currently active site, determine
     * which types should be indeterminate. A type is only indeterminate if at least one
     * corresponding child is checked, but not all.
     */
    const currentSiteIndeterminateTypes = computed(() => {
        const types = [];

        if (
            currentSiteCheckedSpaces.value.length !== 0 &&
            currentSiteCheckedSpaces.value.length !==
                currentSiteSpaces.value.length
        ) {
            types.push(LinkedToType.SPACES);
        }

        if (
            currentSiteCheckedAssets.value.length !== 0 &&
            currentSiteCheckedAssets.value.length !==
                currentSiteAssets.value.length
        ) {
            types.push(LinkedToType.ASSETS);
        }

        return types;
    });

    /**
     * Determine which navigation step is currently active.
     */
    const currentStep = computed(() => {
        return navigationState.steps[navigationState.steps.length - 1] ?? null;
    });

    /**
     * Determine which space or asset group is active and acts as the parent for the next step.
     */
    const currentStepParentId = computed(() => {
        return (
            navigationState.parentIds[navigationState.parentIds.length - 1] ??
            null
        );
    });

    /**
     * Determine which children to show for the current step, based on whether
     * spaces or assets were selected in a previous step. Also take parent selections
     * into consideration.
     */
    const currentStepChildren = computed(() => {
        if (currentStep.value === LinkedToStep.SPACES) {
            return currentSiteSpaces.value.filter(
                (child) => child.parentId === currentStepParentId.value
            );
        }

        if (currentStep.value === LinkedToStep.ASSETS) {
            return currentSiteAssets.value.filter(
                (child) => child.parentId === currentStepParentId.value
            );
        }

        return [];
    });

    /**
     * Determine which sites have been checked.
     */
    const checkedSiteIds = computed(() => {
        return Object.keys(internalState.value).filter(
            (siteId) => internalState.value[siteId].site
        );
    });

    /**
     * Determine which sites should be marked as indeterminate. A site is only indeterminate
     * when at least one of its children is checked, but not the site itself.
     */
    const indeterminateSiteIds = computed(() => {
        if (!sites.value) {
            return [];
        }

        return Object.keys(internalState.value).filter((siteId) => {
            return (
                !internalState.value[siteId].site &&
                (internalState.value[siteId][LinkedToType.SPACES].length > 0 ||
                    internalState.value[siteId][LinkedToType.ASSETS].length > 0)
            );
        });
    });

    /**
     * Determine which spaces, asset and asset groups to show for the current search query.
     */
    const searchResults = computed(() => {
        if (!currentSite.value) {
            return [];
        }

        return currentSite.value.children.filter(
            (child) =>
                child.name
                    .toLowerCase()
                    .includes(searchQuery.value.toLowerCase()) ||
                child.identifier
                    ?.toLowerCase()
                    .includes(searchQuery.value.toLowerCase())
        );
    });

    /**
     * Based on previous step, determine which navigation step should be shown next.
     */
    const handleStepForward = (choice) => {
        if (currentStep.value === LinkedToStep.SITES) {
            navigationState.siteId = choice;
            navigationState.steps.push(LinkedToStep.TYPES);

            if (!Object.hasOwn(internalState.value, choice)) {
                internalState.value[choice] = {
                    [LinkedToType.SITE]: false,
                    [LinkedToType.SPACES]: [],
                    [LinkedToType.ASSETS]: [],
                };
            }

            return;
        }

        if (
            currentStep.value === LinkedToStep.TYPES &&
            choice === LinkedToType.SPACES
        ) {
            navigationState.steps.push(LinkedToStep.SPACES);
            return;
        }

        if (
            currentStep.value === LinkedToStep.TYPES &&
            choice === LinkedToType.ASSETS
        ) {
            navigationState.steps.push(LinkedToStep.ASSETS);
            return;
        }

        if (
            currentStep.value === LinkedToStep.SPACES ||
            currentStep.value === LinkedToStep.ASSETS
        ) {
            if (!currentSiteParentIds.value.includes(choice)) {
                return;
            }

            navigationState.parentIds.push(choice);
            return;
        }

        throw Error("Could not determine the next step.");
    };

    /**
     * Based on current step, determine which navigation step to go back to.
     */
    const handleStepBack = () => {
        if (currentStep.value === LinkedToStep.SITES) {
            return;
        }

        if (currentStep.value === LinkedToStep.SEARCH) {
            searchQuery.value = "";
            navigationState.steps.pop();
            return;
        }

        if (currentStep.value === LinkedToStep.TYPES) {
            navigationState.steps.pop();
            return;
        }

        if (
            currentStepParentId.value &&
            (currentStep.value === LinkedToStep.SPACES ||
                currentStep.value === LinkedToStep.ASSETS)
        ) {
            navigationState.parentIds.pop();
            return;
        }

        if (
            !currentStepParentId.value &&
            (currentStep.value === LinkedToStep.SPACES ||
                currentStep.value === LinkedToStep.ASSETS)
        ) {
            navigationState.steps.pop();
            return;
        }

        throw Error("Could not determine the previous step.");
    };

    /**
     * Mark children as checked depending on which step is currently active and
     * which choice was made.
     */
    const handleCheck = (choice) => {
        if (currentStep.value === LinkedToStep.SEARCH) {
            const { child, checked } = choice;

            if (
                child.type !== ChildType.SPACE &&
                child.type !== ChildType.ASSET &&
                child.type !== ChildType.ASSET_GROUP
            ) {
                throw Error(`The type ${child.type} is not known.`);
            }

            const siteId = child.tenantId;
            const childType =
                child.type === ChildType.SPACE
                    ? LinkedToType.SPACES
                    : LinkedToType.ASSETS;

            if (!checkDescendants) {
                if (checked) {
                    internalState.value[siteId][childType].push(child.id);
                    return;
                }

                internalState.value[siteId][childType] = internalState.value[
                    siteId
                ][childType].filter((childId) => childId !== child.id);

                return;
            }

            const site = sites.value.find((site) => site.id === siteId);

            const children =
                childType === LinkedToStep.SPACES
                    ? site.children.filter(
                          (child) => child.type === ChildType.SPACE
                      )
                    : site.children.filter(
                          (child) =>
                              child.type === ChildType.ASSET ||
                              child.type === ChildType.ASSET_GROUP
                      );

            const descendants = findDescendants(children, child.id);
            descendants.push(child);

            if (checked) {
                descendants.forEach((descendant) => {
                    if (
                        !internalState.value[siteId][childType].includes(
                            (childId) => descendant.id === childId
                        )
                    ) {
                        internalState.value[siteId][childType].push(
                            descendant.id
                        );
                    }
                });

                return;
            }

            internalState.value[siteId][childType] = internalState.value[
                siteId
            ][childType].filter(
                (childId) =>
                    !descendants.some((descendant) => childId === descendant.id)
            );

            return;
        }

        if (currentStep.value === LinkedToStep.SITES) {
            const { site, checked } = choice;

            if (!Object.hasOwn(internalState.value, site.id)) {
                internalState.value[site.id] = {
                    [LinkedToType.SITE]: false,
                    [LinkedToType.SPACES]: [],
                    [LinkedToType.ASSETS]: [],
                };
            }

            if (!checkDescendants) {
                internalState.value[site.id][LinkedToType.SITE] = checked;
                return;
            }

            const children = sites.value.find((s) => s.id === site.id).children;
            const spaces = children
                .filter((child) => child.type === ChildType.SPACE)
                .map((child) => child.id);

            const assets = children
                .filter(
                    (child) =>
                        child.type === ChildType.ASSET ||
                        child.type === ChildType.ASSET_GROUP
                )
                .map((child) => child.id);

            if (checked) {
                internalState.value[site.id] = {
                    [LinkedToType.SITE]: true,
                    [LinkedToType.SPACES]: spaces,
                    [LinkedToType.ASSETS]: assets,
                };

                return;
            }

            internalState.value[site.id] = {
                [LinkedToType.SITE]: false,
                [LinkedToType.SPACES]: [],
                [LinkedToType.ASSETS]: [],
            };

            return;
        }

        if (currentStep.value === LinkedToStep.TYPES) {
            const { type, checked } = choice;

            if (
                type !== LinkedToType.SPACES &&
                type !== LinkedToType.ASSETS &&
                type !== LinkedToType.SITE
            ) {
                throw Error(`The type ${type} is not known.`);
            }

            if (type === LinkedToType.SITE) {
                internalState.value[currentSite.value.id][LinkedToType.SITE] =
                    checked;

                return;
            }

            const children =
                type === LinkedToType.SPACES
                    ? currentSiteSpaces.value
                    : currentSiteAssets.value;

            if (checked) {
                internalState.value[currentSite.value.id][type] = children.map(
                    (child) => child.id
                );

                return;
            }

            internalState.value[currentSite.value.id][type] = [];

            return;
        }

        if (
            currentStep.value === LinkedToStep.SPACES ||
            currentStep.value === LinkedToStep.ASSETS
        ) {
            const { child, checked } = choice;

            const type =
                currentStep.value === LinkedToStep.SPACES
                    ? LinkedToType.SPACES
                    : LinkedToType.ASSETS;

            if (!checkDescendants) {
                if (checked) {
                    internalState.value[currentSite.value.id][type].push(
                        child.id
                    );
                    return;
                }

                internalState.value[currentSite.value.id][type] =
                    internalState.value[currentSite.value.id][type].filter(
                        (childId) => childId !== child.id
                    );

                return;
            }

            const children =
                currentStep.value === LinkedToStep.SPACES
                    ? currentSiteSpaces.value
                    : currentSiteAssets.value;

            const descendants = findDescendants(children, child.id);
            descendants.push(child);

            if (checked) {
                descendants.forEach((descendant) => {
                    if (
                        !internalState.value[currentSite.value.id][
                            type
                        ].includes((childId) => descendant.id === childId)
                    ) {
                        internalState.value[currentSite.value.id][type].push(
                            descendant.id
                        );
                    }
                });

                return;
            }

            internalState.value[currentSite.value.id][type] =
                internalState.value[currentSite.value.id][type].filter(
                    (childId) =>
                        !descendants.some(
                            (descendant) => childId === descendant.id
                        )
                );

            return;
        }

        throw Error("Could not determine what has been checked.");
    };

    const handleSearch = (query) => {
        const hasPreviousQuery = !!searchQuery.value;
        const hasNewQuery = !!query;

        searchQuery.value = query;

        if (!hasPreviousQuery && hasNewQuery) {
            navigationState.steps.push(LinkedToStep.SEARCH);
            return;
        }

        if (hasPreviousQuery && !hasNewQuery) {
            navigationState.steps.pop();
        }
    };

    /**
     * Determine if any children have been selected for any of the available sites.
     */
    const hasSelectionBeenMade = computed(() => {
        for (const siteId in internalState.value) {
            const hasSpaces =
                internalState.value[siteId][LinkedToType.SPACES].length > 0;
            const hasAssets =
                internalState.value[siteId][LinkedToType.ASSETS].length > 0;
            const hasSite =
                internalState.value[siteId][LinkedToType.SITE] === true;

            if (hasSpaces || hasAssets || hasSite) {
                return true;
            }
        }

        return false;
    });

    /**
     * Get the IDs of all sites and children that have been checked.
     */
    const getState = () => {
        const children = [];
        const sites = [];

        Object.entries(internalState.value).forEach(([siteId, state]) => {
            if (state.site) {
                sites.push(siteId);
            }

            children.push(...state.spaces, ...state.assets);
        });

        return {
            sites,
            children,
        };
    };

    /**
     * Get all sites and children that have been checked.
     */
    const getChecked = () => {
        const _children = [];
        const _sites = [];

        Object.entries(internalState.value).forEach(([siteId, state]) => {
            const site = sites.value.find((site) => site.id === siteId);

            if (state.site) {
                _sites.push(site);
            }

            const children = [...state.spaces, ...state.assets];
            _children.push(
                ...site.children.filter((child) => children.includes(child.id))
            );
        });

        return {
            sites: _sites,
            children: _children,
        };
    };

    return {
        currentStep,
        currentStepChildren,
        currentSiteParentIds,
        currentSiteCheckedChildrenIds,
        currentSiteCheckedTypes,
        currentSiteIndeterminateChildrenIds,
        currentSiteIndeterminateTypes,
        checkedSiteIds,
        indeterminateSiteIds,
        searchQuery,
        searchResults,
        hasSelectionBeenMade,
        handleStepForward,
        handleStepBack,
        handleCheck,
        handleSearch,
        getState,
        getChecked,
    };
};
